import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle,
  Clock,
  Target,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';

interface CaseStudy {
  id: string;
  title: string;
  industry: string;
  service: string;
  timeline: string;
  keyResult: string;
  challenge: string;
  solution: string;
  results: string[];
  sections: {
    title: string;
    content: string[];
  }[];
}

const caseStudies: CaseStudy[] = [
  {
    id: 'churn-prediction',
    title: 'Validating Churn Prediction Feasibility',
    industry: 'B2B SaaS',
    service: 'Churn Diagnostics & Research',
    timeline: '8 weeks',
    keyResult: 'Determined whether customer behavior could reliably predict churn, providing a foundation for future retention strategy',
    challenge: 'A B2B SaaS company had 2000+ contracts expiring over the next two years. Historical churn was low, so leadership assumed retention was under control. But they had no way to predict which accounts would actually renew. The customer success team was reactive—discovering problems only when contracts came up for renewal, often too late to act.',
    solution: 'We conducted an 8-week diagnostic research engagement to determine whether customer behavior patterns could predict who would renew versus who would leave.',
    results: [
      'Validated that churn prediction was feasible with available data',
      'Found that recent behavior is more predictive than historical averages',
      'Identified that usage trends matter more than absolute usage levels',
      'Established foundation for building an operational retention system'
    ],
    sections: [
      {
        title: 'The Research Process',
        content: [
          'We analyzed three years of product usage data across 200+ contracts to understand what usage patterns looked like for customers who renewed versus those who churned.',
          'An early discovery: the company\'s systems didn\'t actually track which customers had churned. Their finance software had contracts. Usage databases had activity. But nowhere did anyone record "this customer renewed" or "this customer left."',
          'So we did the foundational work—reviewing contracts, talking to customer success leaders, building a complete historical record of who renewed and who didn\'t. This became the ground truth for all analysis.'
        ]
      },
      {
        title: 'Key Findings',
        content: [
          'Finding #1: Recent Behavior Matters Most — How customers used the product in the months before renewal was significantly more predictive than their average usage over the entire contract.',
          'Finding #2: Trends Matter More Than Absolutes — A customer with low usage but increasing activity is lower risk than a customer with high usage but declining engagement.',
          'Finding #3: Usage Data Alone Isn\'t Enough — Behavioral data tells part of the story, but relationship quality, contract context, and business dynamics also matter.'
        ]
      },
      {
        title: 'Business Decision',
        content: [
          'Rather than deploying a pure prediction model, we recommended combining research findings with an operational system that incorporates CSM judgment alongside quantitative signals.',
          'Recent usage trends inform the analysis, but relationship quality and business context guide the final decision. This hybrid approach proved more practical than a black-box algorithm.'
        ]
      }
    ]
  },
  {
    id: 'operational-prevention',
    title: 'Building Operational Churn Prevention',
    industry: 'B2B SaaS',
    service: 'Risk Scoring & Operations',
    timeline: '8 weeks',
    keyResult: 'Created a system that gives CSMs clear daily guidance on which accounts need attention',
    challenge: 'After validating that churn prediction was feasible, the company faced a practical problem: How do you actually use that research? The customer success team still had no daily system for identifying risk. Renewals were still being discovered reactively. CSMs had no clear way to prioritize among 2000+ contracts.',
    solution: 'We designed an operational risk-scoring system that identifies at-risk contracts, is transparent so CSMs understand why accounts are flagged, and guides daily work.',
    results: [
      'Full visibility into renewal pipeline (vs. scattered guesses before)',
      'Automated monthly reporting (eliminated hours of manual work)',
      'Clear prioritization framework for CSM outreach',
      'Early warning system for at-risk accounts'
    ],
    sections: [
      {
        title: 'How It Works',
        content: [
          'Every month, the system analyzes customer usage trends and assigns each contract a risk category:',
          'CRITICAL - Very low usage, immediate attention needed.',
          'DECLINING - Usage dropping, proactive outreach required.',
          'AT RISK - Warning signs present, worth monitoring closely.',
          'HEALTHY - Stable engagement, standard renewal process.',
          'GROWTH - Strong recent activity, potential expansion opportunity.'
        ]
      },
      {
        title: 'Why This Approach Works',
        content: [
          'It\'s transparent. CSMs don\'t trust black-box systems. With clear rules, they can see the logic and trust it.',
          'It\'s practical. A simple system that gets used beats a sophisticated system that doesn\'t.',
          'It automates drudgery. CSMs focus on relationships, not data work.',
          'It gives leadership visibility. For the first time, the company can answer "How many contracts are at risk?" with confidence.'
        ]
      },
      {
        title: 'What Changed',
        content: [
          'Before: CSMs manually reviewed contracts, leadership had limited visibility, risk was discovered at renewal, no clear prioritization.',
          'After: Risk identified systematically, clear visibility into pipeline health, CSMs know where to focus, single source of truth for renewal status.'
        ]
      }
    ]
  },
  {
    id: 'unified-analytics',
    title: 'Unified Renewal Analytics',
    industry: 'B2B SaaS',
    service: 'Analytics & Reporting',
    timeline: '4 weeks',
    keyResult: 'Single source of truth for all renewal data with accurate monthly reporting',
    challenge: 'With the risk scoring system now identifying at-risk accounts, leadership needed visibility across the entire business. But renewal data lived in silos: the CRM tracked pipeline but couldn\'t distinguish renewals from new business. Finance software had contracts but no renewal status field. Regional spreadsheets existed separately. Month-end close was chaotic—teams spent hours reconciling different numbers.',
    solution: 'Instead of forcing data into systems it doesn\'t belong in, we built a consolidation layer that integrates with the risk scoring system. A master document pulls together fragmented renewal data, normalizes it, and feeds clean analytics to decision-makers.',
    results: [
      'Significant improvement in data accuracy',
      'Eliminated reconciliation conflicts between teams',
      'Reduced time spent on manual data consolidation',
      'Better strategic decisions based on reliable data'
    ],
    sections: [
      {
        title: 'The Architecture',
        content: [
          'Layer 1: Regional Renewal Data — Each region maintains contracts with status classification (Renewed, Churned, In Progress, Overdue).',
          'Layer 2: Smart Consolidation — Master document pulls all regional data and applies consistent metrics.',
          'Layer 3: Analytics & Reporting — Consolidated data feeds dashboards with company-level, regional, and product views.'
        ]
      },
      {
        title: 'What Leaders Can Now See',
        content: [
          'Year-to-date renewal counts and revenue impact.',
          'Progress against quarterly targets by region.',
          'Renewal rates by product line.',
          'Actionable intelligence that was previously hidden in spreadsheets.'
        ]
      },
      {
        title: 'Regional Performance Insights',
        content: [
          'The dashboard revealed performance differences across regions that weren\'t visible before.',
          'Some regions consistently outperformed others. The data helped identify what was working and where additional support was needed.',
          'Before the consolidated view, these insights were hidden. Now they drive strategic decisions.'
        ]
      }
    ]
  },
  {
    id: 'automation',
    title: 'Automating Renewal Operations',
    industry: 'B2B SaaS',
    service: 'Operations Automation',
    timeline: '6 weeks',
    keyResult: 'Eliminated manual reporting work with reliable automated processes',
    challenge: 'The risk scoring and analytics systems were working, but still required manual effort. Every month, someone had to run queries, execute scripts, update dashboards, and monitor for errors. The system was fragile—it worked only because one person knew how everything connected.',
    solution: 'We engineered the entire operation—risk scoring, data consolidation, and reporting—as a single automated system. On a regular schedule, the system collects data, calculates metrics, assigns risk categories, runs quality checks, generates reports, and alerts the team if anything fails.',
    results: [
      'Eliminated monthly manual reporting work',
      'Scalable architecture that handles growth',
      'Automated monitoring and alerting',
      'Built-in data quality checks'
    ],
    sections: [
      {
        title: 'Built-In Quality Checks',
        content: [
          'Automated tests run after every calculation:',
          '• Every contract gets exactly one risk category',
          '• Risk categories are valid and consistent',
          '• No contracts disappear between runs',
          '• All required fields are populated',
          '• Results are stable and within expected ranges'
        ]
      },
      {
        title: 'What This Enables',
        content: [
          'For Operations: CSMs see current information, not stale monthly batches.',
          'For Finance: Forecasts based on actual current risk assessments.',
          'For Leadership: Reliable data instead of guesses. Early warning when things change.',
          'For Growth: System scales with the business without additional manual effort.'
        ]
      },
      {
        title: 'Operational Excellence',
        content: [
          'The system runs reliably without manual intervention.',
          'Alerts notify the team if anything goes wrong.',
          'Complete audit trail of all changes.',
          'Documentation ensures anyone can understand and maintain it.',
          'This is production-grade operations, not best-effort manual processes.'
        ]
      }
    ]
  }
];

function CaseStudyCard({ study, isExpanded, onToggle }: {
  study: CaseStudy;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <Card variant="bordered" padding="none" className="overflow-hidden">
      {/* Header - Always visible */}
      <div className="p-6 lg:p-8">
        <div className="flex flex-wrap gap-2 mb-4">
          <Badge variant="info">{study.industry}</Badge>
          <Badge variant="default">{study.service}</Badge>
          <Badge variant="amber" className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {study.timeline}
          </Badge>
        </div>

        <h2 className="text-2xl md:text-3xl font-heading font-black text-obsidian dark:text-signal mb-4">
          {study.title}
        </h2>

        <div className="flex items-start gap-3 p-4 bg-amber/10 border border-amber/20 rounded mb-6">
          <Target className="w-5 h-5 text-amber flex-shrink-0 mt-0.5" />
          <p className="text-slate-700 dark:text-slate-300 font-medium">
            {study.keyResult}
          </p>
        </div>

        {/* Challenge & Solution Summary */}
        <div className="space-y-4 mb-6">
          <div>
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2">
              The Challenge
            </h3>
            <p className="text-slate-600 dark:text-slate-400">
              {study.challenge}
            </p>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2">
              The Solution
            </h3>
            <p className="text-slate-600 dark:text-slate-400">
              {study.solution}
            </p>
          </div>
        </div>

        {/* Results */}
        <div className="grid sm:grid-cols-2 gap-3 mb-6">
          {study.results.map((result, idx) => (
            <div key={idx} className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-green-500 dark:text-green-400 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-slate-600 dark:text-slate-400">{result}</span>
            </div>
          ))}
        </div>

        {/* Expand/Collapse Button */}
        <Button
          variant="outline"
          onClick={onToggle}
          rightIcon={isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        >
          {isExpanded ? 'Show less' : 'Read full case study'}
        </Button>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-slate-200 dark:border-slate-700 p-6 lg:p-8 bg-slate-50 dark:bg-obsidian">
          <div className="space-y-8">
            {study.sections.map((section, idx) => (
              <div key={idx}>
                <h3 className="text-xl font-heading font-black text-obsidian dark:text-signal mb-4">
                  {section.title}
                </h3>
                <div className="space-y-3">
                  {section.content.map((paragraph, pIdx) => (
                    <p key={pIdx} className="text-slate-600 dark:text-slate-400">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

export function ProjectsPage() {
  const [expandedStudies, setExpandedStudies] = useState<Set<string>>(new Set());

  const toggleStudy = (id: string) => {
    setExpandedStudies(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-white dark:bg-obsidian">
      {/* Hero */}
      <section className="section-padding bg-slate-50 dark:bg-obsidian">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-heading font-black text-obsidian dark:text-signal">
              Case Studies
            </h1>
            <p className="mt-4 text-lg text-slate-600 dark:text-slate-400">
              How we helped a B2B SaaS company transform from reactive churn management
              to proactive, data-driven retention operations.
            </p>
          </div>
        </div>
      </section>

      {/* Journey Overview */}
      <section className="border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0F1014]">
        <div className="container-custom py-8">
          <div className="grid md:grid-cols-4 gap-4 text-center">
            <div className="p-4">
              <div className="text-3xl font-black text-amber mb-1">1</div>
              <div className="text-sm font-medium text-obsidian dark:text-signal">Research & Validation</div>
              <div className="text-xs text-slate-500">Prove feasibility</div>
            </div>
            <div className="p-4">
              <div className="text-3xl font-black text-amber mb-1">2</div>
              <div className="text-sm font-medium text-obsidian dark:text-signal">Operational System</div>
              <div className="text-xs text-slate-500">Build risk scoring</div>
            </div>
            <div className="p-4">
              <div className="text-3xl font-black text-amber mb-1">3</div>
              <div className="text-sm font-medium text-obsidian dark:text-signal">Unified Analytics</div>
              <div className="text-xs text-slate-500">Single source of truth</div>
            </div>
            <div className="p-4">
              <div className="text-3xl font-black text-amber mb-1">4</div>
              <div className="text-sm font-medium text-obsidian dark:text-signal">Full Automation</div>
              <div className="text-xs text-slate-500">Eliminate manual work</div>
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="section-padding bg-white dark:bg-obsidian">
        <div className="container-custom">
          <div className="space-y-8">
            {caseStudies.map((study) => (
              <CaseStudyCard
                key={study.id}
                study={study}
                isExpanded={expandedStudies.has(study.id)}
                onToggle={() => toggleStudy(study.id)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-slate-100 dark:bg-[#0F1014] border-t border-slate-200 dark:border-slate-800">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-black text-obsidian dark:text-signal">
            Ready to transform your retention operations?
          </h2>
          <p className="mt-4 text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Whether you need to validate if churn prediction is feasible, build operational
            systems, or automate your renewal process. Let's talk.
          </p>
          <div className="mt-8">
            <Link to="/contact">
              <Button
                size="lg"
                rightIcon={<ArrowRight className="w-5 h-5" />}
              >
                Book a diagnostic
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ProjectsPage;
